import { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react"

// Firebase utilities
import { db, funEU } from "../../../../utils/firebase"

// Providers
import { AlertsContext } from "../../../../utils/providers/alerts"

// Shared, full airport list (same data the quoting flight/transfer flow uses)
import AIRPORTS from "../../../../containers/enquiry/_components/new-quote/data/airports.json"
import { nearestAirportCode } from "./airport-coords"

// UI components
import Input from "../../../ui/inputs/input"
import SearchSelect from "../../../ui/select/search-select"
import { SearchIcon, PinIcon, PlusIcon, TimesIcon } from "../../../../utils/svgs"

import "./location-features.scss"

// Address lookups are proxied through the isolated `functions-lookups` codebase
// (europe-west2) so the Google Maps key stays server-side — no client env var.
const placesAutocompleteCallable = funEU.httpsCallable("placesAutocomplete")
const placeDetailsCallable = funEU.httpsCallable("placeDetails")

// A throwaway token tying an autocomplete burst to its placeDetails follow-up so
// Google bills them as one session. Regenerated after every resolved lookup.
const makeSessionToken = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`

// Pretty one-line label for a chosen airport, e.g. "Faro (FAO) · Portugal".
// The stored name is the airports.json value ("Faro Airport, PT"); we tidy the
// name, expand the country code, and append the IATA code.
const formatAirportLabel = (airport) => {
	if (!airport?.code) return ""

	const raw = airport.name || ""
	const lastComma = raw.lastIndexOf(",")
	let name = (lastComma >= 0 ? raw.slice(0, lastComma) : raw).trim()
	const countryCode = lastComma >= 0 ? raw.slice(lastComma + 1).trim() : ""

	// Trim a trailing "Airport" / "International Airport" for a cleaner read.
	name = name.replace(/\s*(international\s+)?airport$/i, "").trim() || name

	let country = countryCode
	try {
		if (countryCode) country = new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode) || countryCode
	} catch (error) {
		country = countryCode
	}

	return `${name} (${airport.code})${country ? ` · ${country}` : ""}`
}

// Curated amenity list. Keys are stable; labels are display-only. Custom
// entries get stored verbatim with `custom: true` so popular ones can be
// promoted into this list later.
const FEATURE_OPTIONS = [
	["all_inclusive", "All inclusive"],
	["indoor_pool", "Indoor pool"],
	["outdoor_pool", "Outdoor pool"],
	["heated_pool", "Heated pool"],
	["spa", "Spa & wellness"],
	["gym", "Gym / fitness"],
	["tennis", "Tennis courts"],
	["kids_club", "Kids club"],
	["adults_only", "Adults only"],
	["restaurant", "Restaurant(s)"],
	["bar", "Bar / lounge"],
	["wifi", "Free WiFi"],
	["parking", "Free parking"],
	["beach_access", "Beach access"],
	["sea_view", "Sea view"],
	["driving_range", "Driving range"],
	["pro_shop", "Pro shop"],
	["buggy_hire", "Buggy hire"],
	["club_hire", "Club hire"],
]

// The new "Location & Features" tab, shared by holidays and courses.
export default function LocationFeatures({ entityKind, entityId, entity }) {
	const isCourse = entityKind === "CMS_COURSE"
	const collectionPath = isCourse ? `CMS_courses/${entityId}` : `CMS_holidays/${entityId}`

	const { pushAlert } = useContext(AlertsContext)

	// Location
	const [location, setLocation] = useState(entity?.location || {})
	const [editingLocation, setEditingLocation] = useState(!entity?.location?.formatted_address)
	const [locationQuery, setLocationQuery] = useState("")
	const [predictions, setPredictions] = useState([])
	const [placesOpen, setPlacesOpen] = useState(false)
	const [searchingPlaces, setSearchingPlaces] = useState(false)
	const sessionTokenRef = useRef(makeSessionToken())
	const placesRef = useRef(null)

	// Features
	const [features, setFeatures] = useState(entity?.features || [])

	// Nearest airport (shared quoting helper)
	const [nearestAirport, setNearestAirport] = useState(entity?.nearest_airport || null)

	// Merge a partial update into the entity document
	const save = useCallback(
		async (partial) => {
			try {
				await db.doc(collectionPath).set(partial, { merge: true })
			} catch (error) {
				pushAlert({ type: "ERROR", title: "Could not save", body: error.message })
			}
		},
		[collectionPath, pushAlert]
	)

	// Debounced address typeahead via the lookups codebase
	useEffect(() => {
		if (!editingLocation) return

		const query = locationQuery.trim()
		if (query.length < 2) {
			setPredictions([])
			return
		}

		setSearchingPlaces(true)
		const handle = setTimeout(async () => {
			try {
				const { data } = await placesAutocompleteCallable({ input: query, sessionToken: sessionTokenRef.current })
				setPredictions(data?.predictions || [])
				setPlacesOpen(true)
			} catch (error) {
				pushAlert({ type: "ERROR", title: "Address search failed", body: error.message })
			} finally {
				setSearchingPlaces(false)
			}
		}, 280)

		return () => clearTimeout(handle)
	}, [locationQuery, editingLocation, pushAlert])

	// Close the predictions dropdown on an outside click
	useEffect(() => {
		const handleClick = (event) => {
			if (placesRef.current && !placesRef.current.contains(event.target)) setPlacesOpen(false)
		}
		document.addEventListener("mousedown", handleClick)
		return () => document.removeEventListener("mousedown", handleClick)
	}, [])

	// Resolve a chosen prediction into a full location object and persist it
	const choosePrediction = async (prediction) => {
		setPlacesOpen(false)
		setSearchingPlaces(true)
		try {
			const { data } = await placeDetailsCallable({ placeId: prediction.place_id, sessionToken: sessionTokenRef.current })
			if (data?.location) {
				setLocation(data.location)
				const updates = { location: data.location }

				// Auto-pick the nearest known airport from the new coordinates.
				// Still editable via the dropdown — this just pre-fills it.
				const code = nearestAirportCode(data.location.lat, data.location.lng)
				if (code && AIRPORTS[code]) {
					const airport = { code, name: AIRPORTS[code] }
					setNearestAirport(airport)
					updates.nearest_airport = airport
				}

				save(updates)
				setEditingLocation(false)
			}
		} catch (error) {
			pushAlert({ type: "ERROR", title: "Could not load that address", body: error.message })
		} finally {
			setSearchingPlaces(false)
			setLocationQuery("")
			setPredictions([])
			sessionTokenRef.current = makeSessionToken()
		}
	}

	// Update a single location field locally and persist on blur
	const updateLocationField = (field, value) => {
		setLocation((current) => ({ ...current, [field]: value }))
	}
	const saveLocation = () => save({ location })

	// Toggle a feature on/off (curated)
	const toggleFeature = (key, label) => {
		setFeatures((current) => {
			const exists = current.some((feature) => feature.key === key)
			const next = exists ? current.filter((feature) => feature.key !== key) : [...current, { key, label, custom: false }]
			save({ features: next })
			return next
		})
	}

	// Add a custom (free-text) feature
	const addCustomFeature = (label) => {
		const trimmed = label.trim()
		if (!trimmed) return

		const key = `custom_${trimmed.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`
		setFeatures((current) => {
			if (current.some((feature) => feature.key === key)) return current
			const next = [...current, { key, label: trimmed, custom: true }]
			save({ features: next })
			return next
		})
	}

	const removeFeature = (key) => {
		setFeatures((current) => {
			const next = current.filter((feature) => feature.key !== key)
			save({ features: next })
			return next
		})
	}

	// Nearest airport (searchable picker). Stores { code, name }.
	const pickAirport = (code, name) => {
		const next = code ? { code, name } : null
		setNearestAirport(next)
		save({ nearest_airport: next })
	}

	return (
		<div className="entity-location-features">
			{/* ---------------------------------------------------------- */}
			{/* LOCATION                                                     */}
			{/* ---------------------------------------------------------- */}
			<div className="elf-section">
				<h3>Location</h3>

				{editingLocation && (
					<>
						<div className="elf-places" ref={placesRef}>
							<label className="elf-label">Search for a place</label>
							<div className="elf-search">
								<span className="elf-search-icon">
									<SearchIcon />
								</span>
								<input
									className="elf-search-input"
									placeholder="Start typing a hotel, resort or course…"
									value={locationQuery}
									onChange={(event) => setLocationQuery(event.target.value)}
									onFocus={() => predictions.length && setPlacesOpen(true)}
								/>
								{searchingPlaces && <span className="elf-search-spinner" />}
							</div>

							{placesOpen && predictions.length > 0 && (
								<div className="elf-places-dropdown">
									{predictions.map((prediction) => (
										<div key={prediction.place_id} className="elf-places-row" onClick={() => choosePrediction(prediction)}>
											<span className="elf-places-pin">
												<PinIcon />
											</span>
											<span className="elf-places-text">{prediction.description}</span>
										</div>
									))}
								</div>
							)}
						</div>

						<div className="input-grid cols-2 elf-grid elf-address-grid">
							<div className="span-2">
								<Input label="Full address" showLabel={true} value={location.formatted_address || ""} onChange={(value) => updateLocationField("formatted_address", value)} onBlur={saveLocation} placeholder="Full address" />
							</div>
							<Input label="Country" showLabel={true} value={location.country || ""} onChange={(value) => updateLocationField("country", value)} onBlur={saveLocation} placeholder="Country" />
							<Input label="Region" showLabel={true} value={location.region || ""} onChange={(value) => updateLocationField("region", value)} onBlur={saveLocation} placeholder="Region" />
							<Input label="Latitude" showLabel={true} value={location.lat ?? ""} onChange={(value) => updateLocationField("lat", value)} onBlur={saveLocation} placeholder="Latitude" />
							<Input label="Longitude" showLabel={true} value={location.lng ?? ""} onChange={(value) => updateLocationField("lng", value)} onBlur={saveLocation} placeholder="Longitude" />
						</div>

						{location?.formatted_address && (
							<button type="button" className="elf-text-button" onClick={() => setEditingLocation(false)}>
								Done
							</button>
						)}
					</>
				)}

				{!editingLocation && (
					<div className="elf-summary">
						<div className="elf-summary-info">
							<b>{location.formatted_address}</b>
							<span>
								{[location.region, location.country].filter(Boolean).join(", ")}
								{location.lat && location.lng ? ` · ${Number(location.lat).toFixed(4)}, ${Number(location.lng).toFixed(4)}` : ""}
								{location.timezone ? ` · ${location.timezone}` : ""}
							</span>
						</div>
						<button type="button" className="ui-button secondary is-small" onClick={() => setEditingLocation(true)}>
							Edit
						</button>
					</div>
				)}
			</div>

			{/* ---------------------------------------------------------- */}
			{/* NEAREST AIRPORT (shared — sits next to the address)          */}
			{/* ---------------------------------------------------------- */}
			<div className="elf-section">
				<h3>Nearest airport</h3>
				<div className="input-grid cols-2 elf-grid">
					<div className="elf-airport-field">
						<AirportPicker key={nearestAirport?.code || "none"} value={nearestAirport} onPick={pickAirport} />
						{nearestAirport?.code && <small className="elf-field-note">{formatAirportLabel(nearestAirport)}</small>}
					</div>
				</div>
			</div>

			{/* ---------------------------------------------------------- */}
			{/* HOTEL: FEATURES                                              */}
			{/* ---------------------------------------------------------- */}
			{!isCourse && (
				<div className="elf-section">
					<h3>Features &amp; amenities</h3>
					<p className="elf-hint">Pick from the list or add your own.</p>
					<FeaturePicker features={features} onToggle={toggleFeature} onAddCustom={addCustomFeature} onRemove={removeFeature} />
				</div>
			)}

		</div>
	)
}

// ---------------------------------------------------------------------------
// Airport picker — searchable typeahead over the app's full airport list.
// Mirrors the capped SearchSelect pattern used in the quoting flight modal so
// the ~7,800-entry list never renders in full.
// ---------------------------------------------------------------------------
function AirportPicker({ value, onPick }) {
	const [typed, setTyped] = useState("")

	const visible = useMemo(() => {
		const query = typed.trim().toUpperCase()
		if (!query) return {}

		const out = {}
		let count = 0
		for (const code of Object.keys(AIRPORTS)) {
			if (count >= 50) break
			if (code.toUpperCase().includes(query) || (AIRPORTS[code] || "").toUpperCase().includes(query)) {
				out[code] = AIRPORTS[code]
				count += 1
			}
		}
		return out
	}, [typed])

	return (
		<SearchSelect
			options={visible}
			withKey
			showLabel
			showClear
			hideArrow
			label="Nearest airport"
			placeholder="Search airport…"
			defaultSelected={value?.code ? { option: value.code, value: value.name } : undefined}
			onTextChange={setTyped}
			onSelect={({ option }) => onPick(option, AIRPORTS[option])}
			clearInput={() => onPick("", "")}
		/>
	)
}

// ---------------------------------------------------------------------------
// Curated + custom feature picker
// ---------------------------------------------------------------------------
function FeaturePicker({ features, onToggle, onAddCustom, onRemove }) {
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState("")
	const wrapperRef = useRef(null)

	// Close the dropdown when clicking outside
	useEffect(() => {
		const handleClick = (event) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false)
		}
		document.addEventListener("mousedown", handleClick)
		return () => document.removeEventListener("mousedown", handleClick)
	}, [])

	const chosenKeys = features.map((feature) => feature.key)
	const lowerQuery = query.toLowerCase()
	const available = FEATURE_OPTIONS.filter(([key, label]) => !chosenKeys.includes(key) && label.toLowerCase().includes(lowerQuery))
	const exactExists = FEATURE_OPTIONS.some(([, label]) => label.toLowerCase() === lowerQuery)

	const commitCustom = () => {
		if (query.trim() && !exactExists) {
			onAddCustom(query)
			setQuery("")
		}
	}

	return (
		<div className="elf-feature-picker" ref={wrapperRef}>
			{/* Add control — a proper input that opens the curated/custom dropdown */}
			<div className="elf-feature-add">
				<input
					value={query}
					placeholder="Add a feature…"
					onChange={(event) => setQuery(event.target.value)}
					onFocus={() => setOpen(true)}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault()
							commitCustom()
						}
					}}
				/>
				<span className="elf-feature-add-icon">
					<PlusIcon />
				</span>

				{open && (
					<div className="elf-tag-dropdown">
						{available.map(([key, label]) => (
							<div key={key} className="elf-tag-opt" onClick={() => onToggle(key, label)}>
								<span className="elf-tag-opt-box" />
								{label}
							</div>
						))}

						{query.trim() && !exactExists && (
							<div className="elf-tag-opt add-custom" onClick={commitCustom}>
								<PlusIcon /> Add “{query.trim()}” as a custom feature
							</div>
						)}

						{available.length === 0 && !query.trim() && <div className="elf-tag-opt is-empty">All listed features added</div>}
					</div>
				)}
			</div>

			{/* Selected features — listed outside the input, 3 columns, hover to remove */}
			{features.length > 0 && (
				<div className="elf-feature-list">
					{features.map((feature) => (
						<div key={feature.key} className={["elf-feature", feature.custom && "is-custom"].filter(Boolean).join(" ")}>
							<span className="elf-feature-check">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
									<path d="M20 6 9 17l-5-5" />
								</svg>
							</span>
							<span className="elf-feature-label">{feature.label}</span>
							<span className="elf-feature-remove" onClick={() => onRemove(feature.key)} title="Remove">
								<TimesIcon />
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	)
}
